import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImageUploader } from '@/components/ui/image-uploader';
import { Loader2, ImageIcon, Trash2, EyeOff, Eye, CloudIcon, InfoIcon } from 'lucide-react';
import { toast } from 'sonner';
import { uploadToCloudinary, getResponsiveImageUrl } from '@/lib/cloudinary';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AspectRatio } from '@/components/ui/aspect-ratio';

interface HeroImage {
  id: string;
  image_url: string;
  title: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

export default function HeroImagesManagement() {
  const [images, setImages] = useState<HeroImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newImageTitle, setNewImageTitle] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [useCloudinary, setUseCloudinary] = useState(true);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('hero_background_images')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setImages(data || []);
    } catch (error: any) {
      console.error('Error fetching hero images:', error.message);
      toast.error('Failed to load hero images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleAddImage = async () => {
    if (!newImageUrl) {
      toast.error('Please upload an image first');
      return;
    }

    setSubmitting(true);
    try {
      const highestOrder = Math.max(0, ...images.map(img => img.display_order));
      
      const { error } = await supabase
        .from('hero_background_images')
        .insert([
          { 
            image_url: newImageUrl, 
            title: newImageTitle || null,
            display_order: highestOrder + 1
          }
        ]);
      
      if (error) throw error;
      
      toast.success('Hero image added successfully');
      setNewImageTitle('');
      setNewImageUrl('');
      fetchImages();
    } catch (error: any) {
      console.error('Error adding hero image:', error.message);
      toast.error('Failed to add hero image');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleImageActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('hero_background_images')
        .update({ is_active: !currentStatus })
        .eq('id', id);
      
      if (error) throw error;
      
      setImages(images.map(img => 
        img.id === id ? { ...img, is_active: !currentStatus } : img
      ));
      
      toast.success(`Image ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error: any) {
      console.error('Error toggling image status:', error.message);
      toast.error('Failed to update image status');
    }
  };

  const deleteImage = async (id: string) => {
    if (!confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const { error } = await supabase
        .from('hero_background_images')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      setImages(images.filter(img => img.id !== id));
      toast.success('Image deleted successfully');
    } catch (error: any) {
      console.error('Error deleting image:', error.message);
      toast.error('Failed to delete image');
    }
  };

  const handleImageUploaded = async (url: string, file?: File) => {
    if (useCloudinary && file) {
      setSubmitting(true);
      try {
        toast.info('Uploading to Cloudinary...');
        const cloudinaryUrl = await uploadToCloudinary(file);
        
        if (cloudinaryUrl) {
          setNewImageUrl(cloudinaryUrl);
          toast.success('Image uploaded to Cloudinary successfully');
        } else {
          setNewImageUrl(url);
          toast.warning('Cloudinary upload failed, using regular upload instead');
        }
      } catch (error) {
        console.error('Error with Cloudinary upload:', error);
        setNewImageUrl(url);
        toast.warning('Cloudinary upload failed, using regular upload instead');
      } finally {
        setSubmitting(false);
      }
    } else {
      setNewImageUrl(url);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Hero Background Images</h1>
      </div>
      
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Add New Background Image</CardTitle>
          <CardDescription>
            Upload high-quality travel images to display in the hero section.
            Peak view images work best.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant={useCloudinary ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUseCloudinary(true)}
                    className="flex items-center gap-2"
                  >
                    <CloudIcon className="h-4 w-4" />
                    Cloudinary Upload
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Images are stored in Cloudinary and automatically optimized.<br/>Max size: 10MB (will be compressed)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant={!useCloudinary ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUseCloudinary(false)}
                    className="flex items-center gap-2"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Standard Upload
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Images are stored in Supabase Storage.<br/>Max size: 5MB</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <InfoIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p><strong>Where are my images stored?</strong></p>
                  <p className="text-sm mt-1">
                    • <strong>Cloudinary Upload:</strong> Images are stored in Cloudinary's cloud service and optimized for web.
                    <br/>
                    • <strong>Standard Upload:</strong> Images are stored in your Supabase project's storage buckets.
                    <br/><br/>
                    Only the image URL is stored in the database, regardless of upload method.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="title">Image Title (Optional)</Label>
            <Input
              id="title"
              placeholder="Mountain Peak View"
              value={newImageTitle}
              onChange={(e) => setNewImageTitle(e.target.value)}
            />
          </div>
          
          <ImageUploader
            onImageUploaded={handleImageUploaded}
            currentImage={newImageUrl}
            label={useCloudinary ? "Upload Background Image with Cloudinary" : "Upload Background Image"}
            aspectRatio={16/9}
            maxWidth={1920}
            maxHeight={1080}
            maxFileSizeMB={10}
            useCloudinary={useCloudinary}
          />
          
          <Button 
            onClick={handleAddImage} 
            disabled={!newImageUrl || submitting}
            className="w-full"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <ImageIcon className="mr-2 h-4 w-4" />
                Add Background Image
              </>
            )}
          </Button>
        </CardContent>
      </Card>
      
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Manage Background Images</CardTitle>
          <CardDescription>
            Images will rotate automatically in the hero section.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No background images added yet. Add some above.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden">
                  <AspectRatio ratio={16/9} className="overflow-hidden bg-muted">
                    <img
                      src={image.image_url.includes('cloudinary.com') 
                        ? getResponsiveImageUrl(image.image_url, 400)
                        : image.image_url}
                      alt={image.title || "Hero background image"}
                      className="object-cover w-full h-full transition-all hover:scale-105"
                    />
                    {image.image_url.includes('cloudinary.com') && (
                      <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center">
                        <CloudIcon className="h-3 w-3 mr-1" />
                        Cloudinary
                      </div>
                    )}
                  </AspectRatio>
                  
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="font-medium truncate">
                        {image.title || "Untitled Image"}
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => toggleImageActive(image.id, image.is_active)}
                          title={image.is_active ? "Deactivate image" : "Activate image"}
                        >
                          {image.is_active ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => deleteImage(image.id)}
                          className="text-destructive"
                          title="Delete image"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {image.is_active ? "Active" : "Inactive"} • Order: {image.display_order}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
